import {IForm} from '../pages/oneAddQuote/oneQuoteTypes'
import {getYearFromEffectiveDate} from '.'
import {METAL_LEVELS} from './catalogs'
export {
  BACKEND_URL,
  FEATURE_SHOW_GOOGLE_INTEGRATION,
  GOOGLE_CLIENT_ID,
  RING_CENTRAL_APP_SERVER,
  RING_CENTRAL_CLIENT_ID,
  VERSION_APP,
} from './appConfig'
export {
  INIT_FORM,
  INIT_FORM_ATTACHMENTS,
  INIT_FORM_EXTRAS,
  INIT_FORM_LIFE_DETAIL,
  INIT_FORM_LIFE_MEDICATION,
  INIT_FORM_MEMBERS,
  INIT_FORM_QUOTE_POLICY,
  INIT_FORMS_PAYMENTS,
} from './policyFormDefaults'
export {
  COUNTRIES,
  DEDUCTION_TYPE_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  IMMIGRATION_STATUS_CHOICES,
  MARITAL_STATUS,
  METAL_LEVELS,
  MONTHS,
  PAYMENT_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS,
  STATUS_POLICY,
  TABS_ONE_USER,
  TEMPLATES,
} from './catalogs'

export const STORAGE_NAMES = {
  TOKEN: 'TOKEN',
  USER: 'USER',
  PERMISSIONS: 'PERMISSIONS',
  ID_USER: 'USER_ID',
  LAST_ROUTE: 'LAST_ROUTE',
  KANBAN_SELECTED_GROUP: 'KANBAN_SELECTED_GROUP',
  KANBAN_SELECTED_WORKFLOW: 'KANBAN_SELECTED_WORKFLOW',
  KANBAN_SHARED_VIEW: 'KANBAN_SHARED_VIEW',
  TOKEN_GOOGLE: 'google_token',
  EMAIL_GOOGLE: 'google_email',
  RING_CENTRAL: 'ringcentral',
  VICIDIAL_SESSION: 'vicidial_session',
  VICIDIAL_FORCE_DISCONNECTED: 'vicidial_force_disconnected',
  VICIDIAL_SELECTED_CAMPAIGN: 'vicidial_selected_campaign',
  VICIDIAL_SELECTED_INGROUPS: 'vicidial_selected_ingroups',
  VICIDIAL_DRAWER_STANDALONE_STATE: 'vicidial_drawer_standalone_state',
  // Sesión del admin guardada mientras se suplanta a otro usuario.
  IMPERSONATION_ORIGIN: 'IMPERSONATION_ORIGIN',
} as const

/**
 * Claves de localStorage atadas a la identidad del usuario logueado (sesiones de
 * telefonía, tokens de terceros). Hay que limpiarlas al entrar y al salir de una
 * suplantación: si sobreviven, el usuario suplantado hereda la sesión de
 * telefonía del admin y puede terminar llamando con el caller ID equivocado.
 */
export const SESSION_SCOPED_STORAGE_KEYS = [
  STORAGE_NAMES.TOKEN_GOOGLE,
  STORAGE_NAMES.EMAIL_GOOGLE,
  STORAGE_NAMES.RING_CENTRAL,
  STORAGE_NAMES.VICIDIAL_SESSION,
  STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED,
  STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN,
  STORAGE_NAMES.VICIDIAL_SELECTED_INGROUPS,
  STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE,
  STORAGE_NAMES.KANBAN_SELECTED_GROUP,
  STORAGE_NAMES.KANBAN_SELECTED_WORKFLOW,
  STORAGE_NAMES.KANBAN_SHARED_VIEW,
  STORAGE_NAMES.LAST_ROUTE,
] as const

export const EVENT_SERVICE_WORKER_NAME = 'swUpdated' as const
export const MSG_SKIP_WAITING = 'SKIP_WAITING' as const

export const COLORS = ['primary', 'success', 'warning', 'danger', 'info', 'dark'] as const

export const bootstrapColorMap: Record<string, string> = {
  primary: '#0d6efd',
  secondary: '#6c757d',
  success: '#198754',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#0dcaf0',
  light: '#f8f9fa',
  dark: '#212529',
  gray: '#adb5bd',

  // Aliases usados en statuses
  blue: '#3e97ff',
  green: '#198754',
  red: '#dc3545',
  orange: '#fd7e14',
  purple: '#6f42c1',
  yellow: '#ffc107',
  accent: '#3e97ff',
}

// Devuelve un hex si es bootstrap, o un color si es válido, o gris como fallback
export function getBootstrapColorHex(color: string): string {
  if (bootstrapColorMap[color]) {
    return bootstrapColorMap[color]
  }

  // Si es un valor válido en CSS (ej: 'green' o '#123456'), lo devolvemos tal cual
  const isCssColor = CSS.supports('color', color)
  return isCssColor ? color : '#adb5bd'
}

export function getContrastTextColor(bgColor: string): string {
  // Convierte el hex a RGB
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Calcula luminancia relativa (receta WCAG)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Si es muy claro → texto negro, si es oscuro → texto blanco
  return luminance > 0.6 ? '#000000' : '#ffffff'
}

export const getPolicyCategory = (
  edges: {node: {code: string; category: string}}[] | undefined,
  code?: string
) => edges?.find((e) => e.node.code === (code ?? ''))?.node.category ?? null

export const ALL_PERMISSIONS = {
  VIEW_USER: 'view_user',
  ADD_USER: 'add_user',
  EDIT_USER: 'change_user',
  IMPERSONATE_USER: 'impersonate_user',

  VIEW_LEAD: 'view_lead',
  ADD_LEAD: 'add_lead',
  DELETE_LEAD: 'delete_lead',
  EDIT_LEAD: 'change_lead',
  EDIT_LEAD_PERSONAL: 'change_lead_personal',
  EXPORT_LEAD: 'export_lead',
  IMPORT_LEAD: 'import_lead',

  VIEW_POLICY: 'view_policy',
  VIEW_ALL_POLICIES: 'view_all_policies',
  VIEW_POLICY_MEMBER_ID_CARD: 'view_policy_member_id_card',
  ADD_POLICY: 'add_policy',
  EDIT_POLICY: 'change_policy',
  VIEW_POLICY_STATUS: 'view_policy_status',
  EXPORT_POLICY: 'export_policy',
  EXPORT_POLICY_FULL: 'export_policy_full',
  IMPORT_POLICIES: 'import_policies',
  MARK_COMPLETED_POLICY: 'mark_completed_policy',
  REASSIGN_POLICY: 'reassign_policy',
  BULK_UPDATE_POLICY: 'bulk_update_policy',
  VIEW_POLICY_PAYMENT: 'view_policy_payment',

  VIEW_SETTING: 'view_setting',

  VIEW_PIPELINE: 'view_pipeline',
  // Solo lectura: el backend devuelve todos los pipelines de la organización y
  // todos sus leads. No habilita la pantalla de configuración de pipelines, que
  // sigue exigiendo VIEW_PIPELINE / EDIT_PIPELINE.
  VIEW_ALL_PIPELINES: 'view_all_pipelines',
  // Habilita asignar revisores y grupos a un pipeline. El backend exige el mismo
  // permiso, así que ocultar el item no es la única defensa.
  EDIT_PIPELINE: 'change_pipeline',
  VIEW_LEAD_SOURCE: 'view_lead_source',
  VIEW_GROUP_ASSIGNMENT: 'view_group_assignment',
  VIEW_LEAD_LOSS_REASON: 'view_lead_loss_reason',
  VIEW_TAG_MANAGER: 'view_tag_manager',

  // Webhooks de captación de leads por etapa del pipeline. El backend exige los
  // mismos codenames (integrations/{google,facebook}_leads/api_views.py), así que
  // ocultar los iconos no es la única defensa.
  VIEW_GOOGLE_LEADS_WEBHOOK: 'view_googleleadswebhookconfig',
  ADD_GOOGLE_LEADS_WEBHOOK: 'add_googleleadswebhookconfig',
  CHANGE_GOOGLE_LEADS_WEBHOOK: 'change_googleleadswebhookconfig',
  DELETE_GOOGLE_LEADS_WEBHOOK: 'delete_googleleadswebhookconfig',
  VIEW_FACEBOOK_LEADS_WEBHOOK: 'view_facebookleadswebhookconfig',
  ADD_FACEBOOK_LEADS_WEBHOOK: 'add_facebookleadswebhookconfig',
  CHANGE_FACEBOOK_LEADS_WEBHOOK: 'change_facebookleadswebhookconfig',
  DELETE_FACEBOOK_LEADS_WEBHOOK: 'delete_facebookleadswebhookconfig',
  VIEW_LOVABLE_LEADS_WEBHOOK: 'view_lovableleadswebhookconfig',
  ADD_LOVABLE_LEADS_WEBHOOK: 'add_lovableleadswebhookconfig',
  CHANGE_LOVABLE_LEADS_WEBHOOK: 'change_lovableleadswebhookconfig',
  DELETE_LOVABLE_LEADS_WEBHOOK: 'delete_lovableleadswebhookconfig',

  VIEW_COMMISSION: 'view_commission_report',
  VIEW_ALL_COMMISSION: 'view_all_commission_report',
  VIEW_COMMISSION_REPORT_DELEGATED: 'view_commission_report_delegated',
  VIEW_COMMISSION_FINDER: 'view_commission_finder',
  ADD_COMMISSION: 'add_commission_report',
  EDIT_COMMISSION: 'change_commission_report',
  DELETE_COMMISSION: 'delete_commission_report',

  VIEW_SIGNATURE_FORMS: 'view_signatureform',
  ADD_SIGNATURE_FORMS: 'add_signatureform',
  EDIT_SIGNATURE_FORMS: 'change_signatureform',
  DELETE_SIGNATURE_FORMS: 'delete_signatureform',

  VIEW_PDF_ENVELOPES: 'view_pdfdocumenttemplate',
  ADD_PDF_ENVELOPES: 'add_pdfdocumenttemplate',
  EDIT_PDF_ENVELOPES: 'change_pdfdocumenttemplate',
  DELETE_PDF_ENVELOPES: 'delete_pdfdocumenttemplate',

  VIEW_CONTACT: 'view_contact',
  ADD_CONTACT: 'add_contact',
  EDIT_CONTACT: 'change_contact',
  DELETE_CONTACT: 'delete_contact',
  VIEW_CONTACT_PREVIEW_ASSIGNED: 'view_contact_preview_assigned',
  // Permiso por defecto de Django sobre core.CommunicationLog. Gatea el Inbox
  // (pantalla, ruta y sección Communication del sidebar) y los logs de
  // comunicación en Contacts / Leads. Hasta la migración 0334 se usaba un
  // view_communication_log declarado a mano que duplicaba a este; esa migración
  // lo borró y traspasó sus asignaciones. Existe también un view_inbox que se
  // eliminó por no estar validado en ninguna parte.
  VIEW_COMMUNICATION_LOGS: 'view_communicationlog',

  VIEW_SMS: 'view_policy',
  VIEW_EMAIL: 'view_policy',

  VIEW_NOTIFICATION: 'view_notification',

  // Communication / Operations menu items (previously ungated)
  VIEW_INBOX: 'view_inbox',
  VIEW_POLICYCHAT: 'view_policychat',
  VIEW_CALENDAR: 'view_calendar',

  // Coverage sweep — previously ungated modules/submodules
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_MEDICARE_CENTER: 'view_medicare_center',
  VIEW_CAMPAIGN_TEMPLATES: 'view_campaign_templates',
  VIEW_CAMPAIGN_RECIPIENTS: 'view_campaign_recipients',
  VIEW_CAMPAIGN_MEDIA: 'view_campaign_media',
  VIEW_CAMPAIGN_AUTOMATIONS: 'view_campaign_automations',
  SELF_PROVISION_NUMBERS: 'self_provision_numbers',
  // Administration (superadmin) — gated as perm OR SuperAdministrador group
  VIEW_ORGANIZATIONS: 'view_organizations',
  VIEW_CHANNELS_CONFIG: 'view_channels_config',
  VIEW_APP_TOKENS: 'view_app_tokens',
  VIEW_SYSTEM_MONITOR: 'view_system_monitor',
  VIEW_POLICYTOOLS_SETTINGS: 'view_policytools_settings',
  MANAGE_EMAIL_SETTINGS: 'manage_email_settings',

  VIEW_INTEGRATION: 'view_integrations',
  ADD_INTEGRATION: 'add_integrations',
  EDIT_INTEGRATION: 'change_integrations',
  DELETE_INTEGRATION: 'delete_integrations',

  VIEW_CARRIER: 'view_insurance_operator',
  VIEW_CARRIER_IMPORT_BATCH: 'view_carrierimportbatch',
  ADD_CARRIER: 'add_insurance_operator',
  EDIT_CARRIER: 'change_insurance_operator',
  DELETE_CARRIER: 'delete_insurance_operator',

  VIEW_POLICY_TYPE: 'view_policy_type',
  ADD_POLICY_TYPE: 'add_policy_type',
  EDIT_POLICY_TYPE: 'change_policy_type',
  DELETE_POLICY_TYPE: 'delete_policy_type',

  VIEW_STATE_EU: 'view_state',
  EDIT_STATE_EU: 'change_state',

  VIEW_COMMISSION_DEDUCTION_TYPE: 'view_commission_deduction_type',
  ADD_COMMISSION_DEDUCTION_TYPE: 'add_commission_deduction_type',
  EDIT_COMMISSION_DEDUCTION_TYPE: 'change_commission_deduction_type',
  DELETE_COMMISSION_DEDUCTION_TYPE: 'delete_commission_deduction_type',

  VIEW_RESOURCE_CENTER: 'view_resource_center',
  ADD_RESOURCE_CENTER: 'add_resource_center',
  EDIT_RESOURCE_CENTER: 'change_resource_center',
  DELETE_RESOURCE_CENTER: 'delete_resource_center',

  VIEW_RESOURCE_CATEGORY: 'view_resource_category',
  ADD_RESOURCE_CATEGORY: 'add_resource_category',
  EDIT_RESOURCE_CATEGORY: 'change_resource_category',
  DELETE_RESOURCE_CATEGORY: 'delete_resource_category',

  VIEW_ACADEMY: 'view_course',
  ADD_COURSE: 'add_course',
  EDIT_COURSE: 'change_course',
  DELETE_COURSE: 'delete_course',
  ADD_LESSON: 'add_lesson',
  EDIT_LESSON: 'change_lesson',
  DELETE_LESSON: 'delete_lesson',
  ADD_QUIZ: 'add_quiz',
  EDIT_QUIZ: 'change_quiz',
  DELETE_QUIZ: 'delete_quiz',

  VIEW_TASKS: 'view_kanbantask',
  ADD_TASKS: 'add_kanbantask',
  EDIT_TASKS: 'change_kanbantask',
  DELETE_TASKS: 'delete_kanbantask',

  VIEW_TICKET: 'view_ticket',
  ADD_TICKET: 'add_ticket',
  CHANGE_TICKET: 'change_ticket',
  DELETE_TICKET: 'delete_ticket',
  ASSIGN_TICKET: 'assign_ticket',
  CHANGE_TICKET_STATUS: 'change_ticket_status',
  COMMENT_TICKET: 'comment_ticket',
  DOWNLOAD_TICKET_ATTACHMENT: 'download_ticket_attachment',
  VIEW_ALL_TICKET: 'view_all_ticket',
  ARCHIVE_TICKET: 'archive_ticket',
  MANAGE_TICKET_SLA: 'manage_ticket_sla',
  VIEW_TICKET_DELEGATED: 'view_ticket_delegated',

  VIEW_CAMPAIGNS: 'view_campaign',

  // Billing (telefonía). Codenames declarados a mano en core.ProvisionedNumber
  // (Meta.permissions), no los defaults de Django. A diferencia del resto del
  // catálogo, billing NO se abre por ser Administrador: hay que chequearlo con
  // `canViewBilling` (helpers), que exige el permiso literal igual que
  // `can_view_billing` en el backend. Usar `hasPermission` acá reintroduce el
  // bypass de admin.
  VIEW_BILLING: 'view_billing',
  MANAGE_BILLING: 'manage_billing',

  // ACA Verification permissions (auto-generated by Django)
  ADD_ACA_VERIFICATION: 'add_policy_aca_verification',
  CHANGE_ACA_VERIFICATION: 'change_policy_aca_verification',
  VIEW_ACA_VERIFICATION: 'view_policy_aca_verification',
} as const

export const INIT_PEOPLE = {
  age: '',
  dob: '',
  gender: 'Male',
  isPregnant: false,
  pregnantWith: '',
  isParent: false,
  usesTobacco: false,
  lastTobaccoUseDate: '',
  hasMec: false,
  aptcEligible: true,
  utilizationLevel: '',
  relationship: '',
  doesNotCohabitate: '',
  currentEnrollment: null,
}

export const SIZE_PAGE_QUICK_QUOTE = 20
export const SIZE_PAGE_DEFAULT = 10
export const SIZE_STEP_PAGINATION = 10

export function getPolicyName(values) {
  const first = values.firstName.trim()
  const middle = values.middleName.trim()
  const last = values.lastName.trim()
  const second = values.secondLastName.trim()

  // Toma la inicial en mayúscula si existe, si no, omite
  const middleInitial = middle ? ' ' + middle : ''
  const secondInitial = second ? ' ' + second : ''
  // const middleInitial = middle ? ` ${middle[0].toUpperCase()}.` : ''
  // const secondInitial = second ? ` ${second[0].toUpperCase()}.` : ''

  // Une los componentes
  return `${first}${middleInitial} ${last}${secondInitial}`.replace(/\s+/g, ' ').trim()
}

export function getMetalLevelValueByLabel(label: string): string | undefined {
  const found = METAL_LEVELS.find((level) => level.label === label)
  return found?.value
}

export function getMetalLevelValue(value: string): string | undefined {
  const found = METAL_LEVELS.find((level) => level.value === value)
  return found?.label
}

export const normalizeAmount = (val: any): string => {
  if (typeof val === 'string') {
    return val.replace(/,/g, '') || '0.00'
  }
  return typeof val === 'number' ? val.toFixed(2) : '0.00'
}

// Bootstrap variant type helper
export type BootstrapVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'

// Map Document status → Bootstrap variant
export const getDocumentVariant = (value?: string): BootstrapVariant => {
  const v = (value || '').toString().toUpperCase()
  switch (v) {
    case 'COMPLETED':
      return 'success' // green
    case 'DECLINED':
      return 'danger' // red
    case 'PROCESSING':
    case 'PENDING':
      return 'warning' // amber
    case 'ACTIVE':
      return 'info' // blue/cyan
    default:
      return 'secondary' // neutral gray
  }
}

// Map Carrier status (Policy.carrier_status_generic) → Bootstrap variant
export const getCarrierStatusVariant = (value?: string | null): BootstrapVariant => {
  switch ((value || '').toString().toUpperCase()) {
    case 'IN_FORCE':
      return 'success'
    case 'TERMINATED':
      return 'danger'
    case 'DELINQUENT':
    case 'PAST_DUE':
    case 'GRACE_PERIOD':
    case 'WARNING':
      return 'warning'
    case 'PENDING_EFFECTUATION':
      return 'info'
    default:
      return 'secondary'
  }
}

/**
 * Color de texto legible para un badge según su variante de fondo.
 *
 * Metronic pisa la paleta de Bootstrap —`info` es violeta, no cyan— pero no ajusta
 * el color del texto, que queda en negro. Sobre los fondos oscuros no se lee, así
 * que hay que forzarlo. `warning`, `light` y `secondary` son suficientemente
 * claros para llevar texto oscuro.
 */
export const getBadgeTextColor = (variant: BootstrapVariant): 'white' | 'dark' =>
  variant === 'warning' || variant === 'light' || variant === 'secondary' ? 'dark' : 'white'

// Map Payment status → Bootstrap variant
export const getPaymentVariant = (value?: string): BootstrapVariant => {
  const v = (value || '').toString().toUpperCase()
  switch (v) {
    case 'PAID':
      return 'success' // green
    case 'FAILED':
      return 'danger' // red
    case 'PENDING':
      return 'warning' // amber
    case 'AUTO_PAY':
      return 'success'
    case 'NOT_APPLICABLE':
      return 'secondary' // neutral gray
    case 'PAGA_POR_SU_CUENTA':
      return 'warning' // amber
    default:
      return 'secondary'
  }
}

export const INIT_CONTACT_INFO = {
  fullName: '',
  firstName: '',
  middleName: '',
  lastName: '',
  secondLastName: '',
  preferredLenguage: '',
  dateBirth: null, // si usas Date, podría ser new Date() o null
  gender: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  stateEUId: '', // ID puede ser string si viene de un select
  zipCode: '',
  countyFips: '',
  immigrationStatus: '',
  annualIncome1: 0,
  annualIncome2: 0,
  annualIncome3: 0,
  isImportant: false,
}

export const INITIAL_CONTACT_FILTERS: {
  name: string
  createdAfter: string
  createdBefore: string
  effectiveDateAfter: string
  effectiveDateBefore: string
  isImportant: undefined
  isArchived: undefined
  isPolicyHolder: undefined
  withoutPolicy: undefined
  withoutLead: undefined
  medicareUnifiedFilter: string
  carrier: string
  policyType: string
  user: string
  assignedTo: string
  zipCode: string
  state: string
  status: string
  policyStatus: string
  myContacts: undefined
  signatureForm: string
  signatureStatus: string
  preferredLanguage: string
  birthMonth: string
  ageFrom: string
  ageTo: string
  gender: string
  immigrationStatus: string | string[]
  policyCreatedFrom: string
  policyCreatedTo: string
  submissionDateFrom: string
  submissionDateTo: string
  completedDateFrom: string
  completedDateTo: string
  organizationId?: string
} = {
  name: '',
  createdAfter: '',
  createdBefore: '',
  effectiveDateAfter: '',
  effectiveDateBefore: '',
  isImportant: undefined,
  isArchived: undefined,
  isPolicyHolder: undefined,
  withoutPolicy: undefined,
  withoutLead: undefined,
  medicareUnifiedFilter: 'ALL_ELIGIBLE',
  carrier: '',
  policyType: '',
  user: '',
  assignedTo: '',
  zipCode: '',
  state: '',
  status: '',
  policyStatus: '',
  myContacts: undefined,
  signatureForm: '',
  signatureStatus: '',
  preferredLanguage: '',
  birthMonth: '',
  ageFrom: '',
  ageTo: '',
  gender: '',
  immigrationStatus: '',
  policyCreatedFrom: '',
  policyCreatedTo: '',
  submissionDateFrom: '',
  submissionDateTo: '',
  completedDateFrom: '',
  completedDateTo: '',
  organizationId: '',
}

export const INITIAL_POLICY_FILTERS: {
  policyName: string
  commissionPaymentStatus: string
  isImportant: undefined
  isArchived: undefined
  isPolicyHolder: undefined
  hasReminders: undefined
  hasMemberId: undefined
  commissionPayable: undefined
  withoutActiveAcaPolicy: undefined
  advisorPaid: undefined
  unassigned: undefined
  ownsCar: undefined
  ownsHome: undefined
  hasAutoPolicy: undefined
  hasHomePolicy: undefined
  hasCarrierAccount: undefined
  accountCreatedByClient: undefined
  carrier: string
  insuranceCategory: string
  policyType: string
  user: string
  assignedTo: string
  zipCode: string
  state: string
  stateEU: string
  stateJurisdictionType: string
  status: string
  documentStatus: string
  paymentStatus: string
  leadSource: string
  signatureForm: string | string[]
  signatureStatus: string
  agentOfRecord: string[]
  immigrationStatus: string
  holderGender: string
  metalLevel: string
  createdAfter: string
  createdBefore: string
  effectiveDateAfter: string
  effectiveDateBefore: string
  documentsDueDateAfter: string
  documentsDueDateBefore: string
  paymentStatusDateAfter: string
  paymentStatusDateBefore: string
  advisorPaidDateAfter: string
  advisorPaidDateBefore: string
  advisorChargeback: undefined
  advisorChargebackDateAfter: string
  advisorChargebackDateBefore: string
  canceledAfter: string
  canceledBefore: string
  completedAfter: string
  completedBefore: string
  effectiveYear: string
  carrierStatusGeneric: string
  carrierStatusRaw: string
  carrierLastAsOfDateAfter: string
  carrierLastAsOfDateBefore: string
  carrierIsAgeIn: undefined
  carrierIsDelinquent: undefined
  carrierIsUnpaidPending: undefined
  carrierIsUnpaidPastDue: undefined
  acaResult: string
  acaValidity: string
} = {
  policyName: '',
  commissionPaymentStatus: '',
  isImportant: undefined,
  isArchived: undefined,
  isPolicyHolder: undefined,
  hasReminders: undefined,
  hasMemberId: undefined,
  commissionPayable: undefined,
  withoutActiveAcaPolicy: undefined,
  advisorPaid: undefined,
  unassigned: undefined,
  ownsCar: undefined,
  ownsHome: undefined,
  hasAutoPolicy: undefined,
  hasHomePolicy: undefined,
  hasCarrierAccount: undefined,
  accountCreatedByClient: undefined,
  carrier: '',
  insuranceCategory: '',
  policyType: '',
  user: '',
  assignedTo: '',
  zipCode: '',
  state: '',
  stateEU: '',
  stateJurisdictionType: '',
  status: '',
  documentStatus: '',
  paymentStatus: '',
  leadSource: '',
  signatureForm: [],
  signatureStatus: '',
  agentOfRecord: [],
  immigrationStatus: '',
  holderGender: '',
  metalLevel: '',
  createdAfter: '',
  createdBefore: '',
  effectiveDateAfter: '',
  effectiveDateBefore: '',
  documentsDueDateAfter: '',
  documentsDueDateBefore: '',
  paymentStatusDateAfter: '',
  paymentStatusDateBefore: '',
  advisorPaidDateAfter: '',
  advisorPaidDateBefore: '',
  advisorChargeback: undefined,
  advisorChargebackDateAfter: '',
  advisorChargebackDateBefore: '',
  canceledAfter: '',
  canceledBefore: '',
  completedAfter: '',
  completedBefore: '',
  effectiveYear: '',
  carrierStatusGeneric: '',
  carrierStatusRaw: '',
  carrierLastAsOfDateAfter: '',
  carrierLastAsOfDateBefore: '',
  carrierIsAgeIn: undefined,
  carrierIsDelinquent: undefined,
  carrierIsUnpaidPending: undefined,
  carrierIsUnpaidPastDue: undefined,
  acaResult: '',
  acaValidity: '',
}

/*
export const INITIAL_POLICY_FILTERS_2: {
  policyName: '',
  carrier: '',
  createdAt: '',
  effectiveDate: '',
  completed: '',
  canceled: '',
  archived: '',
  isImportant: false,
  isArchived: false,
  isReminders: false,
  policyType: '',
  user: '',
  agent: '',
  status: '',
  stateEU: '',
  assignedTo: '',
  zipCode: '',
  effectiveYear: '',
  insuranceCategory: '',
}*/

const DEFAULT_EFFECTIVE_DATE = new Date().toISOString().slice(0, 10)

export const INIT_IFORM: IForm = {
  queryType: 'individual',
  effectiveDate: DEFAULT_EFFECTIVE_DATE,
  year: getYearFromEffectiveDate(DEFAULT_EFFECTIVE_DATE),
  householdIncome: '0',
  market: 'FFM',
  countyfips: '',
  countyfipsName: '',
  state: 'FL',
  zipCode: '',
  limit: 10,
  offset: 0,
  order: 'asc',
  utilizationLevel: '',
  numberOfMembers: '1',
  personOne: {
    age: '',
    dob: '',
    gender: '',
    isPregnant: false,
    pregnantWith: '',
    isParent: false,
    usesTobacco: false,
    lastTobaccoUseDate: '',
    hasMec: true,
    aptcEligible: true,
    utilizationLevel: '',
    relationship: 'self',
    doesNotCohabitate: '',
    currentEnrollment: null,
  },
  people: [],
}
